import {SIN_RESPUESTA, scaleForItem} from '@/lib/bateria'
import {recorteCaido} from '@/lib/recortes-caidos'
import type {components} from '@/lib/api-schema'

export type TarjetaCola = components['schemas']['TarjetaRead']
export type SeccionClave = components['schemas']['SeccionCola']
export type ColaRead = components['schemas']['ColaRead']
export type SeccionCola = components['schemas']['SeccionRead']
export type ResumenCola = components['schemas']['ResumenRead']
export type CalibracionMotor = components['schemas']['CalibracionRead']
export type DecisionCola = components['schemas']['DecisionCola']
export type CandidatoIdentidad = components['schemas']['CandidatoIdentidad']

/** Espeja el orden del servidor; se repite porque el riel pinta todas aunque vengan en cero. `sin_recorte`
 * va de última: agrupa lo que hay que leer en la hoja completa, para no cortar la revisión rápida. */
export const ORDEN_SECCIONES: SeccionClave[] = [
  'ubicar',
  'marcas_5',
  'marcas_4',
  'confirmar',
  'escribir',
  'sin_recorte',
]

/** Sobre esta proporción de dudas, un re-escaneo arregla en minutos lo que la revisión en horas (G6). */
const FRENO_RATIO = 0.4

export function loteDegradado(resumen: ResumenCola | undefined): boolean {
  return !!resumen && resumen.ratio_del_lote > FRENO_RATIO
}

/** De `marca:{paquete}:{item}`; `null` en `ubicar:{cuarentena}`: una hoja sin ubicar no es de nadie. */
export function paqueteDeRef(ref: string): string | null {
  const partes = ref.split(':')
  return partes.length === 3 ? partes[1] : null
}

export function claveDeRef(ref: string): string | null {
  const partes = ref.split(':')
  return partes.length === 3 ? partes[2] : null
}

/** El formato que espera `_acotar` del backend en `solo=`. */
export function refsDeUbicar(cuarentenaIds: string[]): string[] {
  return cuarentenaIds.map((id) => `ubicar:${id}`)
}

export function tarjetasDeSeccion(tarjetas: TarjetaCola[], seccion: SeccionClave): TarjetaCola[] {
  return tarjetas.filter((t) => t.seccion === seccion)
}

/** Para pintar el teclado de la sección siguiente antes de entrar; sale de `scaleForItem` para no duplicar
 * la escala. */
export function escalaDeSeccion(seccion: SeccionClave): readonly string[] | null {
  if (seccion === 'marcas_5') return scaleForItem('intra_001')
  if (seccion === 'marcas_4') return scaleForItem('estres_001')
  // `sin_recorte` mezcla las dos escalas: cada tarjeta trae la suya.
  return null
}

/** `null` = el servidor dice que no es medible, que no es 0%. La precisión subestima (se mide sobre lo
 * dudoso) y el recall depende de lo auditado: quien los pinte tiene que decirlo. */
export function pctDeRatio(ratio: number | null | undefined): number | null {
  return ratio === null || ratio === undefined ? null : Math.round(ratio * 100)
}

/** Solo las familias con algo medido: «Ficha 0%» sin revisiones diría que el lector falló siempre. */
export function familiasMedidas(calibracion: CalibracionMotor): {familia: string; pct: number}[] {
  return calibracion.por_familia
    .filter((f) => f.precision !== null)
    .map((f) => ({familia: f.familia, pct: Math.round(f.precision! * 100)}))
}

export type PaqueteDestino = {id: string; employee_name: string; forma: string}

/** El servidor ya resolvió el candidato; acá solo se confirma que el paquete sigue en el roster: asignar al
 * paquete equivocado es peor que dejar la hoja en cuarentena. */
export function resolverCandidato<T extends PaqueteDestino>(candidato: CandidatoIdentidad, paquetes: T[]): T | null {
  return paquetes.find((p) => p.id === candidato.paquete_id) ?? null
}

type EstadoDecision = 'pendiente' | 'enviada' | 'conflicto'

/** Vive aunque el POST falle. En un conflicto guarda las dos: `decision` es lo que esta persona tecleó y
 * `ganador` lo que de verdad quedó guardado. */
export type DecisionLocal = {
  decision: DecisionCola
  estado: EstadoDecision
  decididoPor?: string | null
  decididoPorNombre?: string | null
  decididoEn?: string | null
  ganador?: string | null
}

const GESTOS: readonly DecisionCola['gesto'][] = ['opcion', 'blanco', 'tachon', 'multiple']

export function decisionesDelServidor(tarjetas: TarjetaCola[]): Record<string, DecisionLocal> {
  const sembradas: Record<string, DecisionLocal> = {}
  for (const tarjeta of tarjetas) {
    if (!tarjeta.decidido) continue
    const valor = tarjeta.decidido.valor
    const gesto = tarjeta.decidido.gesto
    const casillas = tarjeta.decidido.casillas
    sembradas[tarjeta.ref] = {
      decision: {
        tipo: tarjeta.tipo,
        ref: tarjeta.ref,
        valor: typeof valor === 'string' ? valor : null,
        // El gesto sale de un JSON guardado: uno desconocido se lee como una opción normal.
        gesto: GESTOS.find((conocido) => conocido === gesto) ?? 'opcion',
        // Sin las casillas no hay con qué repintar un tachón al volver sobre la tarjeta.
        casillas: Array.isArray(casillas) ? casillas.filter((casilla) => typeof casilla === 'string') : [],
        fuente: 'revision',
        forzar: false,
      },
      estado: 'enviada',
    }
  }
  return sembradas
}

/** La fuente sale del estado de la tarjeta, no del alcance: `revision` solo si el motor preguntaba. Mezclarla
 * con `revision_a_demanda` arruina la medición del motor (`bateria_revision.DecisionFuente`). */
export function decisionBase(tarjeta: TarjetaCola): DecisionCola {
  return {
    tipo: tarjeta.tipo,
    ref: tarjeta.ref,
    valor: null,
    gesto: 'opcion',
    casillas: [],
    fuente: tarjeta.estado === 'pendiente' ? 'revision' : 'revision_a_demanda',
    forzar: false,
  }
}

/** Solo agrega decisiones por `ref`: la cola congelada no se re-baraja ni gana tarjetas a mitad de sesión.
 * Devuelve el mismo arreglo si no hay novedades, para no repintar. */
export function mezclarDecididos(
  congeladas: TarjetaCola[],
  frescas: Pick<TarjetaCola, 'ref' | 'decidido'>[]
): TarjetaCola[] {
  const decididas = new Map<string, TarjetaCola['decidido']>()
  for (const fresca of frescas) if (fresca.decidido) decididas.set(fresca.ref, fresca.decidido)
  let hubo = false
  const mezcladas = congeladas.map((tarjeta) => {
    const decidido = decididas.get(tarjeta.ref)
    if (decidido === undefined || tarjeta.decidido) return tarjeta
    hubo = true
    return {...tarjeta, decidido}
  })
  return hubo ? mezcladas : congeladas
}

/** Apila al final: el servidor ordenó la lista completa y el cursor es un ordinal sobre ella. Deduplica por
 * `ref` porque un tramo puede llegar dos veces; `secciones` es la del primer tramo, ya completa. */
export function unirTramo(previa: ColaRead, tramo: ColaRead): ColaRead {
  const vistas = new Set(previa.tarjetas.map((t) => t.ref))
  const nuevas = tramo.tarjetas.filter((t) => !vistas.has(t.ref))
  if (nuevas.length === 0 && tramo.next_cursor === previa.next_cursor) return previa
  return {
    secciones: previa.secciones,
    tarjetas: nuevas.length === 0 ? previa.tarjetas : [...previa.tarjetas, ...nuevas],
    next_cursor: tramo.next_cursor,
  }
}

/** Del servidor, no de lo cargado. Antes del primer tramo solo hay resumen, que describe el lote: ahí el
 * campo es `pendientes`, o el riel encogería al cargar. */
export function totalesDeSecciones(
  secciones: SeccionCola[],
  campo: 'total' | 'pendientes' = 'total'
): Partial<Record<SeccionClave, number>> {
  return Object.fromEntries(secciones.map((seccion) => [seccion.clave, seccion[campo]]))
}

/** Por `(tipo, ref)`, gana lo último: un reintento no duplica ni pierde la corrección hecha encima. */
export function mezclarPendientes(cola: DecisionCola[], nuevas: DecisionCola[]): DecisionCola[] {
  const porRef = new Map<string, DecisionCola>()
  for (const decision of [...cola, ...nuevas]) porRef.set(`${decision.tipo}:${decision.ref}`, decision)
  return [...porRef.values()]
}

/** `null` = no queda nada por decidir. Comparte el predicado `decidida` con el riel: si contaran distinto,
 * la pantalla diría «Revisión terminada» con trabajo pendiente. */
export function primeraPendiente(tarjetas: TarjetaCola[], decidida: (ref: string) => boolean): CursorCola | null {
  for (const clave of ORDEN_SECCIONES) {
    const suyas = tarjetasDeSeccion(tarjetas, clave)
    const indice = suyas.findIndex((tarjeta) => !decidida(tarjeta.ref))
    if (indice !== -1) return {fase: 'tarjetas', seccion: clave, indice}
  }
  return null
}

/** Siempre hacia adelante y sin dar la vuelta: con navegación posicional, volver al principio llevaría la
 * mano lejos de donde trabajaba. */
export function siguienteTarjeta(
  tarjetas: TarjetaCola[],
  desde: number,
  resueltas: (ref: string) => boolean
): number | null {
  for (let i = desde + 1; i < tarjetas.length; i += 1) {
    if (!resueltas(tarjetas[i].ref)) return i
  }
  return null
}

/** Primero lo que queda de la sección en curso y después las siguientes, en el orden del riel. El predicado
 * lo pone quien llama: es `duda_pendiente` menos lo que esta sesión ya resolvió. */
export function siguientePorMarcar(
  tarjetas: TarjetaCola[],
  desde: {seccion: SeccionClave | null; indice: number},
  porMarcar: (tarjeta: TarjetaCola) => boolean
): CursorCola | null {
  const arranque = desde.seccion === null ? 0 : ORDEN_SECCIONES.indexOf(desde.seccion)
  for (let paso = arranque; paso < ORDEN_SECCIONES.length; paso += 1) {
    const clave = ORDEN_SECCIONES[paso]
    const suyas = tarjetasDeSeccion(tarjetas, clave)
    const desdeIndice = paso === arranque && desde.seccion !== null ? desde.indice + 1 : 0
    const indice = suyas.findIndex((tarjeta, i) => i >= desdeIndice && porMarcar(tarjeta))
    if (indice !== -1) return {fase: 'tarjetas', seccion: clave, indice}
  }
  return null
}

/** Una tarjeta, el cierre de una sección o el cierre del lote; es lo que mueven `←`/`→`. */
export type CursorCola = {
  fase: 'tarjetas' | 'cierre' | 'lote'
  seccion: SeccionClave | null
  indice: number
}

/** El único orden de la pantalla: `←` y `→` son −1 y +1 sobre esta lista. Las secciones vacías no ponen nodos. */
function nodosDeLaCola(tarjetas: TarjetaCola[]): CursorCola[] {
  const nodos: CursorCola[] = []
  for (const clave of ORDEN_SECCIONES) {
    const suyas = tarjetasDeSeccion(tarjetas, clave)
    if (suyas.length === 0) continue
    for (let indice = 0; indice < suyas.length; indice += 1) nodos.push({fase: 'tarjetas', seccion: clave, indice})
    nodos.push({fase: 'cierre', seccion: clave, indice: suyas.length - 1})
  }
  nodos.push({fase: 'lote', seccion: null, indice: 0})
  return nodos
}

/** Posicional a propósito, en las dos direcciones: aterrizar en una tarjeta decidida la muestra con su
 * decisión. `null` = ese lado se acabó y la pantalla no se mueve. */
export function moverCursor(tarjetas: TarjetaCola[], cursor: CursorCola, delta: 1 | -1): CursorCola | null {
  const nodos = nodosDeLaCola(tarjetas)
  const donde = nodos.findIndex(
    (nodo) =>
      nodo.fase === cursor.fase &&
      nodo.seccion === cursor.seccion &&
      (nodo.fase !== 'tarjetas' || nodo.indice === cursor.indice)
  )
  if (donde === -1) return null
  return nodos[donde + delta] ?? null
}

/** El número de la tarjeta es su posición 1-based dentro de la sección; fuera de rango, `null`. */
export function indiceDeNumero(total: number, texto: string): number | null {
  const numero = Number(texto.trim())
  if (texto.trim() === '' || !Number.isInteger(numero) || numero < 1 || numero > total) return null
  return numero - 1
}

/** A ~1 decisión por segundo, dieciséis dan ~16 s de colchón sin traer el lote entero, que pelearía ancho
 * de banda con el recorte en pantalla. */
export const PRECARGA_ADELANTE = 16

/** Con `←`/`→` posicionales, devolverse para reconfirmar es parte del flujo normal. */
export const PRECARGA_ATRAS = 4

/** Con pocas descargas simultáneas, el orden de la lista es la prioridad: lo inmediato de adelante va primero. */
const PRECARGA_INMEDIATA = 4

/** Los tres modos viajan por `crop_download_url`; los PDF de página y cuadernillo se abren aparte. */
function imagenesDeTarjeta(tarjeta: TarjetaCola | undefined): string[] {
  return tarjeta?.crop_download_url ? [tarjeta.crop_download_url] : []
}

/** En orden de prioridad, incluida la posición `desde` para poder decodificarla antes de pintar. No muta
 * `yaPedidas`: decide y devuelve, sin esconder estado en el módulo. */
export function recortesPorPrecargar(
  tarjetas: TarjetaCola[],
  desde: number,
  yaPedidas: ReadonlySet<string>,
  adelante: number = PRECARGA_ADELANTE,
  atras: number = PRECARGA_ATRAS
): string[] {
  const posiciones: number[] = [desde]
  for (let salto = 1; salto <= Math.min(PRECARGA_INMEDIATA, adelante); salto += 1) posiciones.push(desde + salto)
  for (let salto = 1; salto <= Math.max(adelante, atras); salto += 1) {
    if (salto <= adelante) posiciones.push(desde + salto)
    if (salto <= atras) posiciones.push(desde - salto)
  }
  const urls: string[] = []
  for (const posicion of posiciones) {
    for (const url of imagenesDeTarjeta(tarjetas[posicion])) {
      if (yaPedidas.has(url) || urls.includes(url)) continue
      urls.push(url)
    }
  }
  return urls
}

/** Más ancho que lo que monta el carrusel (`pila-de-recortes.tsx`): una ráfaga de key-repeat (10-30
 * tarjetas/s) no alcanza el final de lo pedido, y treinta URLs son ~300 KB. */
const PRECARGA_CORTES = 30

/** Marcas sin recorte vivo (los PNG se borran a los 30 días, `@/lib/recortes-caidos`). Solo hacia adelante:
 * atrás ya está en el caché, y la posición del cursor la pide la tarjeta misma. */
export function cortesPorPrecargar(
  tarjetas: TarjetaCola[],
  desde: number,
  cuantas: number = PRECARGA_CORTES
): {paqueteId: string; itemId: string}[] {
  const objetivos: {paqueteId: string; itemId: string}[] = []
  for (let salto = 1; salto <= cuantas; salto += 1) {
    const tarjeta = tarjetas[desde + salto]
    if (!tarjeta || tarjeta.tipo !== 'marca') continue
    if (tarjeta.crop_download_url && !recorteCaido(tarjeta.crop_download_url)) continue
    const paqueteId = paqueteDeRef(tarjeta.ref)
    const itemId = claveDeRef(tarjeta.ref)
    if (paqueteId === null || itemId === null) continue
    objetivos.push({paqueteId, itemId})
  }
  return objetivos
}

/** `anios` es una opción de la interfaz, nunca un valor que viaje: se guardan sus dígitos y el servidor
 * traduce las tres (`bateria_ficha.valores_de_antiguedad`). */
const ANTIGUEDAD_MENOS_1 = 'menos_1'
export const ANTIGUEDAD_ANIOS = 'anios'
const ANTIGUEDAD_MAS_1 = 'mas_1'
export const OPCIONES_ANTIGUEDAD: string[] = [ANTIGUEDAD_MENOS_1, ANTIGUEDAD_ANIOS, ANTIGUEDAD_MAS_1]

/** El sentinela `anios` no es una respuesta real y también se excluye. */
export function esNumeroDeAnios(valor: string | null): boolean {
  return (
    valor !== null &&
    valor !== '' &&
    valor !== ANTIGUEDAD_MENOS_1 &&
    valor !== ANTIGUEDAD_MAS_1 &&
    valor !== ANTIGUEDAD_ANIOS
  )
}

/** Espejo de `app/core/bateria_ficha.py::_ANIOS_ANTIGUEDAD_MAX`. */
const ANIOS_ANTIGUEDAD_MAX = 80

/** Acepta coma y fraccionarios («1,5 años»). `null` bloquea el Enter en vez de mandar un valor que el
 * servidor rechazaría minutos después en la cola de sincronización. */
export function normalizarAnios(texto: string): string | null {
  const normalizado = texto.trim().replace(',', '.')
  if (normalizado === '') return null
  const numero = Number(normalizado)
  if (!Number.isFinite(numero) || numero <= 0 || numero >= ANIOS_ANTIGUEDAD_MAX) return null
  return normalizado
}

/** `null` para `opcion` y `blanco`, que ya se ven en la casilla marcada. */
export type GestoGuardado = 'tachon' | 'multiple' | null

export function gestoGuardadoDe(local: DecisionLocal | undefined): GestoGuardado {
  const gesto = local?.decision.gesto
  return gesto === 'tachon' || gesto === 'multiple' ? gesto : null
}

/** Vacío sin gesto que repintar: pintar las casillas de una opción limpia inventaría un tachón. */
export function casillasGuardadasDe(local: DecisionLocal | undefined): readonly string[] {
  return gestoGuardadoDe(local) === null ? [] : (local?.decision.casillas ?? [])
}

/** `''` es el chip de «acá no hay dato» y `null` es «nada marcado»: un tachón o una doble marca guardan
 * `valor: null` pero no son un blanco. */
export function seleccionDeCampo(local: DecisionLocal | undefined): string | null {
  if (!local) return null
  if (local.estado === 'conflicto') return local.ganador ?? null
  if (local.decision.gesto === 'blanco') return ''
  return local.decision.valor ?? null
}

/** Lo decidido en esta sesión manda entero sobre la propuesta del OCR, sea numérico o no. */
export function borradorDeAntiguedad(tarjeta: TarjetaCola, local: DecisionLocal | undefined): string {
  if (local) {
    const decidido = seleccionDeCampo(local)
    return esNumeroDeAnios(decidido) ? decidido! : ''
  }
  return esNumeroDeAnios(tarjeta.valor_propuesto) ? tarjeta.valor_propuesto! : ''
}

/** «En blanco» se guarda como `valor: null` + `gesto: 'blanco'`: leyendo solo el valor se pintaría sin contestar. */
export function seleccionDeMarca(local: DecisionLocal | undefined): string | null {
  if (!local) return null
  // En un conflicto se pinta lo que quedó guardado, no lo que esta persona tecleó.
  if (local.estado === 'conflicto') return local.ganador ?? null
  return local.decision.gesto === 'blanco' ? SIN_RESPUESTA : (local.decision.valor ?? null)
}

/** Un conflicto va en `enConflicto` y no en `hechas`: contarlo sería «12 de 12» sobre otra decisión. */
export type AvanceSeccion = {
  clave: SeccionClave
  total: number
  hechas: number
  dejadas: number
  enConflicto: number
}

/** Los `totales` del servidor ganan sobre lo cargado, o el riel crecería bajo la mano mientras llegan
 * tramos. Lo hecho, lo dejado y los conflictos sí salen de lo cargado. */
export function avancesDeSecciones(
  tarjetas: TarjetaCola[],
  decisiones: Record<string, DecisionLocal>,
  dejadas: Record<string, true>,
  totales: Partial<Record<SeccionClave, number>> = {}
): AvanceSeccion[] {
  return ORDEN_SECCIONES.map((clave) => {
    const suyas = tarjetasDeSeccion(tarjetas, clave)
    const estados = suyas.map((tarjeta) => decisiones[tarjeta.ref]?.estado)
    return {
      clave,
      total: totales[clave] ?? suyas.length,
      hechas: estados.filter((estado) => estado !== undefined && estado !== 'conflicto').length,
      dejadas: suyas.filter((tarjeta) => tarjeta.ref in dejadas).length,
      enConflicto: estados.filter((estado) => estado === 'conflicto').length,
    }
  })
}

/** Una sección con algo dejado para después no puede parecer terminada. */
export function tonoDeSeccion(avance: AvanceSeccion): 'green' | 'amber' | 'neutral' {
  if (avance.total === 0) return 'green'
  if (avance.enConflicto > 0) return 'amber'
  if (avance.hechas === avance.total) return 'green'
  return avance.dejadas > 0 ? 'amber' : 'neutral'
}
